import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform } from '../../../types/Platforms';
import { SupplierLogo } from '../../ui/SupplierLogo';

interface Props {
  onSelect: (platform: Platform) => void;
}

// Object.values(Platform) includes the fromString function from the namespace — filter to strings only
const ALL_PLATFORMS = Object.values(Platform).filter((v): v is Platform => typeof v === 'string');

const SelectSupplierStep: React.FC<Props> = ({ onSelect }) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    if (!search.trim()) return ALL_PLATFORMS;
    const q = search.toLowerCase();
    return ALL_PLATFORMS.filter((p) => p.toLowerCase().includes(q));
  }, [search]);

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-text dark:text-text-dark">{t('wizard.selectSupplier.title')}</h2>
        <p className="text-muted dark:text-muted-dark mt-2">{t('wizard.selectSupplier.subtitle')}</p>
      </div>

      <input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder={t('wizard.selectSupplier.searchPlaceholder')}
        className="w-full px-4 py-3 rounded-xl border border-zinc-300 dark:border-zinc-600 bg-background dark:bg-background-dark text-text dark:text-text-dark"
      />

      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3 max-h-[400px] overflow-y-auto pr-1">
        {filtered.map((platform) => (
          <button
            key={platform}
            onClick={() => onSelect(platform)}
            className="flex flex-col items-center justify-center rounded-xl border-2 border-zinc-200 dark:border-zinc-700 hover:border-emerald-500 dark:hover:border-emerald-500 transition-all p-3 aspect-square"
          >
            <SupplierLogo platform={platform} alt={platform} size="huge" />
            <span className="text-[10px] text-muted dark:text-muted-dark mt-2 text-center leading-tight truncate w-full">
              {platform.replace(/_/g, ' ')}
            </span>
          </button>
        ))}
        {filtered.length === 0 && (
          <p className="col-span-full text-center text-muted dark:text-muted-dark py-8">
            {t('wizard.selectSupplier.noResults')}
          </p>
        )}
      </div>
    </div>
  );
};

export default SelectSupplierStep;
